<template>
  <div v-clickoutside="_handleClose" :class="classes">
    <div ref="reference"
         :class="selectionClass"
         @click="$_toggleMenu"
         @mouseenter="onSelfMouseenter"
         @mouseleave="onSelfMouseleave">
      <slot name="input">
        <ul v-if="multiple && !collapseTags" class="list-inline">
          <li v-for="(item, index) in selectedMultiple" :key="index" class="list-inline-item">
            <span class="tag tag--text">
              <span class="tag__title">{{ item.label }}</span>
              <i class="aid aid-close tag__clear"
                 @click.stop="onClearTagClick(index)"></i>
            </span>
          </li>
        </ul>
        <ul v-if="multiple && collapseTags && selectedMultiple.length" class="list-inline">
          <li v-if="selectedMultiple[0]" class="list-inline-item">
            <span class="tag tag--text">
              <span class="tag__title">{{ selectedMultiple[0].label }}</span>
              <i class="aid aid-close tag__clear"
                 @click.stop="onClearTagClick(0)"></i>
            </span>
          </li>
          <li v-if="selectedMultiple.length > 1" class="list-inline-item">
            <span class="tag tag--text">
              <span class="tag__title">+{{ selectedMultiple.length - 1 }}</span>
            </span>
          </li>
        </ul>
        <span v-show="showPlaceholder && !filterable" :class="[prefixSelfClass + '__placeholder']">{{ localePlaceholder }}</span>
        <span v-show="!showPlaceholder && !multiple && !filterable" :class="[prefixSelfClass + '__selected-value']">{{ selectedSingle }}</span>
        <input v-if="filterable"
               ref="input"
               v-model="query"
               :class="[prefixSelfClass + '__input']"
               :placeholder="showPlaceholder ? localePlaceholder : ''"
               :disabled="disabled"
               type="text"
               @keydown="onInputKeydown"
               @keyup.delete="onInputDelete"/>
        <span :class="[prefixSelfClass + '__selection-icon']">
          <i v-show="showCloseIcon"
             :class="`${classMain} ${prefixIconClass}close-circle`"
             style="cursor: pointer"
             @click.stop="clearSingleSelect"></i>
          <i v-show="showArrow" v-if="!remote" :class="`${classMain} ${prefixIconClass}chevron-down`" data-ani-icon></i>
        </span>
      </slot>
    </div>
    <t-pop-box v-show="dropShow" ref="drop" :placement="placement" :style-class="prefixSelfClass + '__menu'" >
      <div v-show="notFoundShow" :class="[prefixSelfClass + '__tips', 'cursor-text']">{{ localeNotFoundText }}</div>
      <template v-show="(!notFound && !remote) || (remote && !loading && !notFound)">
        <ul :class="[prefixSelfClass + '__options']">
          <slot></slot>
          <li v-if="loadMore && !loading && !query" :class="prefixSelfClass + '__item'" @click.stop="onLoadMore">{{ localeLoadMoreText }}</li>
        </ul>
      </template>
      <div v-show="loading" :class="[prefixSelfClass + '__tips', 'cursor-text']">{{ localeLoadingText }}</div>
    </t-pop-box>
  </div>
</template>
<script>
import TIcon from '../icon'
import TPopBox from '../pop-box/pop-box.vue'
import clickoutside from '../../directives/clickoutside'
import Emitter from '../../mixins/emitter'
import Locale from '../../mixins/locale'
import { PREFIX_CLASS, PREFIX_ICON_CLASS, CLASS_MAIN } from '../../utils/constants'

const PREFIX_SELECT_CLASS = `${PREFIX_CLASS}select`

export default {
  name: 'TSelect',
  components: {TIcon, TPopBox},
  directives: {clickoutside},
  mixins: [ Emitter, Locale ],
  props: {
    collapseTags: Boolean,
    value: {
      type: [ String, Number, Object, Array ],
      default: ''
    },
    multiple: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String
    },
    loadMore: {
      type: Boolean,
      default: false
    },
    loadMoreText: {
      type: String,
      default: ''
    },
    filterable: {
      type: Boolean,
      default: false
    },
    filterMethod: {
      type: Function
    },
    remote: {
      type: Boolean,
      default: false
    },
    remoteMethod: {
      type: Function,
      default: () => {}
    },
    loading: {
      type: Boolean,
      default: false
    },
    loadingText: {
      type: String
    },
    size: {
      type: String,
      validator (value) {
        return [ 'sm', 'lg' ].includes(value)
      }
    },
    labelInValue: {
      type: Boolean,
      default: false
    },
    notFoundText: {
      type: String
    },
    placement: {
      type: String,
      validator (value) {
        return [ 'top', 'top-start', 'top-end', 'bottom', 'bottom-start', 'bottom-end' ].includes(value)
      },
      default: 'bottom'
    },
    autoComplete: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      prefixIconClass: PREFIX_ICON_CLASS,
      classMain: CLASS_MAIN,
      prefixSelfClass: PREFIX_SELECT_CLASS,
      visible: false,
      options: [],
      optionInstances: [],
      selectedSingle: '',
      selectedMultiple: [],
      originalFocusIndex: 0,
      focusIndex: 0,
      query: '',
      lastQuery: '',
      multipleDelete: false,
      selectToChangeQuery: false, // when select an option, set this first and set query, because query is watching, it will emit event
      inputLength: 20,
      notFound: false,
      slotChangeDuration: false, // if slot change duration and in multiple, set true and after slot change, set false
      model: this.value,
      showArrow: true,
      isFocused: false
    }
  },
  computed: {
    prevFocusIndex () {
      let index = (this.focusIndex === this.options.length) ? 1 : index
      return index
    },
    classes () {
      return [
        `${PREFIX_SELECT_CLASS}`,
        {
          [`${PREFIX_SELECT_CLASS}--disabled`]: this.disabled,
          [`${PREFIX_SELECT_CLASS}--open`]: this.visible,
          [`${PREFIX_SELECT_CLASS}--${this.size}`]: this.size,
          [`${PREFIX_SELECT_CLASS}--multiple`]: this.multiple,
          [`${PREFIX_SELECT_CLASS}--focus`]: this.isFocused
        }
      ]
    },
    selectionClass () {
      return {
        [`${PREFIX_SELECT_CLASS}__selection`]: !this.autoComplete,
        [`input--disabled`]: this.disabled
      }
    },
    showPlaceholder () {
      let status = false
      if ((typeof this.model) === 'string') {
        if (this.model === '') {
          status = true
        }
      } else if (Array.isArray(this.model)) {
        if (!this.model.length) {
          status = true
        }
      } else if ((typeof this.model) === 'object' && this.labelInValue) {
        if (!this.model.label && !this.model.value) {
          status = true
        }
      }
      return status
    },
    showCloseIcon () {
      return this.disabled === false && !this.multiple && this.clearable && !this.showPlaceholder && !this.showArrow
    },
    localePlaceholder () {
      if (this.placeholder === undefined) {
        return this.t('t.select.placeholder')
      } else {
        return this.placeholder
      }
    },
    localeNotFoundText () {
      if (this.notFoundText === undefined) {
        return this.t('t.select.noMatch')
      } else {
        return this.notFoundText
      }
    },
    localeLoadingText () {
      if (this.loadingText === undefined) {
        return this.t('t.select.loading')
      } else {
        return this.loadingText
      }
    },
    localeLoadMoreText () {
      if (!this.loadMoreText) {
        return this.t('t.select.loadMore')
      } else {
        return this.loadMoreText
      }
    },
    transitionName () {
      return this.placement === 'bottom' ? 'slide-up' : 'slide-down'
    },
    dropShow () {
      let status = true
      const options = this.$slots.default || []
      if (!this.loading && this.remote && this.query === '' && !options.length) status = false
      if (this.autoComplete && !options.length) status = false
      return this.visible && status
    },
    notFoundShow() {
      const options = this.$slots.default || []
      return !!(this.notFoundText && !this.remote && !options.length) || (this.remote && !this.loading && !options.length)
    }
  },
  watch: {
    value (val) {
      this.model = val
      if (val === '' || val === {}) this.query = ''
    },
    model (val, oldVal) {
      this.$emit('input', val)
      this.$_modelToQuery()
      if (this.multiple) {
        this.$_updateOptions(true)
        if (this.slotChangeDuration) {
          this.slotChangeDuration = false
        } else {
          this.$_updateMultipleSelected()
        }
      } else {
        this.$_updateOptions(true)
        this.$_updateSingleSelected()
      }
    },
    visible (val) {
      if (val) {
        if (this.filterable) {
          if (this.multiple) {
            this.$refs.input.focus()
          } else {
            if (!this.autoComplete) this.$refs.input.select()
          }
          if (this.remote) {
            this.$_findChild(child => {
              if (this.labelInValue) {
                if (this.multiple) {
                  let flag = false
                  for (var i = 0; i < this.model.length; i++) {
                    if (this.model[i].value === child.value) {
                      flag = true
                      break
                    }
                  }
                  child.selected = flag
                } else {
                  child.selected = (this.model.value === child.value)
                }
              } else {
                child.selected = this.multiple ? this.model.indexOf(child.value) > -1 : this.model === child.value
              }
            })
            let options = this.$slots.default || []
            if (this.query !== '' && !options.length) {
              this.remoteMethod(this.query)
            }
          }
        }
        this.broadcast('TPopBox', 'on-update-popper')
      } else {
        if (this.filterable) {
          if (!this.autoComplete) this.$refs.input.blur()
          setTimeout(() => {
            if (this.query === '') {
              this.$_broadcastQuery('')
            }
          }, 300)
        }
        this.broadcast('TPopBox', 'on-destroy-popper')
      }
    },
    query (val, oldVal) {
      if (!oldVal && val && this.multiple) {
        this.multipleDelete = false
      }
      if (this.remote && this.remoteMethod) {
        if (!this.selectToChangeQuery) {
          this.$emit('on-query-change', val)
          this.remoteMethod(val)
        }
        this.focusIndex = 0
        this.originalFocusIndex = 0
        this.$_findChild(child => {
          child.isFocus = false
        })
        this.$_updateOptions(true)
      } else {
        if (!this.selectToChangeQuery) {
          this.$emit('on-query-change', val)
        }
        this.$_broadcastQuery(val)
        let isHidden = true
        this.$nextTick(() => {
          this.$_findChild((child) => {
            if (child.visible) {
              isHidden = false
            }
          })
          this.notFound = isHidden
        })
      }
      this.selectToChangeQuery = false
      this.broadcast('TPopBox', 'on-update-popper')
    },
    filterable (val) {
      this.enableFocusStyle(!val)
    }
  },
  mounted () {
    if (!this.multiple && this.model) {
      this.query = this.model
      this.lastQuery = this.model
    }
    this.$_modelToQuery()
    this.$_updateOptions(true)
    document.addEventListener('keydown', this.onDocumentKeydown)
    this.$on('item-mounted', (item) => {
      let val = this.model
      if (this.labelInValue) {
        val = this.model.value
      }
      if (!this.remote) {
        this.$_modelToQuery()
        if (this.query === '' && val === item.value) {
          if (item.label) {
            this.query = item.label
          } else if (item.searchLabel) {
            this.query = item.searchLabel
          } else {
            this.query = item.value
          }
        }
        if (this.filterable) {
          this.$_broadcastQuery('') // 隐藏所有选项
        }
      } else {
        if (this.labelInValue) {
          if (this.multiple) {
            let flag = false
            for (let i = this.model.length - 1; i >= 0; i--) {
              if (this.model[i].value === item.value) {
                flag = true
                break
              }
            }
            item.selected = flag
          } else {
            item.selected = (this.model.value === item.value)
          }
        } else {
          item.selected = this.multiple ? this.model.indexOf(item.value) > -1 : this.model === item.value
        }
      }
      if (this.multiple === false && item.selected) {
        // if (this.hasGroup()) {
        //   this.focusIndex = this.$children[item.groupId].$children.indexOf(item) + 1
        // } else {
        // }
        this.focusIndex = this.optionInstances.indexOf(item) + 1
        this.originalFocusIndex = this.focusIndex
      }
      this.$_slotChange()
      this.$_updateOptions(true, true)
    })
    this.$on('on-select-selected', (valueObj) => {
      if (this.model === valueObj.value || this.model.value === valueObj.value) {
        if (this.autoComplete) this.$emit('on-change', valueObj.value)
        this.$_hideMenu()
      } else {
        if (this.multiple) {
          let index = -1
          if (this.labelInValue) {
            let flag = false
            for (var i = 0; i < this.model.length; i++) {
              if (valueObj.value === this.model[i].value) {
                flag = true
                index = i
                break
              }
            }
            if (flag) {
              this._removeTag(index)
            } else {
              this.model.push(valueObj)
              this.broadcast('TPopBox', 'on-update-popper')
            }
          } else {
            index = this.model.indexOf(valueObj.value)
            if (index >= 0) {
              this._removeTag(index)
            } else {
              this.model.push(valueObj.value)
              this.broadcast('TPopBox', 'on-update-popper')
            }
          }
          if (this.filterable) {
            if (this.query !== '') this.selectToChangeQuery = true
            this.query = ''
            this.focus()
          }
        } else {
          this.$_hideMenu()
          if (this.labelInValue) {
            this.model = valueObj
          } else {
            this.model = valueObj.value
          }
          this.focusIndex = this.optionInstances.indexOf(valueObj.instance) + 1
          this.originalFocusIndex = this.focusIndex
          if (this.filterable) {
            this.$_findChild((child) => {
              if (child.value === valueObj.value) {
                if (this.query !== '') {
                  this.selectToChangeQuery = true
                }
                this.lastQuery = this.query = child.label === undefined ? child.searchLabel : child.label
              }
            })
          }
        }
      }
      if (this.multiple === false) {
        this.$emit('on-select-selected-with-index', this.optionInstances.indexOf(valueObj.instance))
      }
      this.focus()
    })
    this.enableFocusStyle(!this.filterable)
  },
  beforeDestroy () {
    document.removeEventListener('keydown', this.onDocumentKeydown)
  },
  methods: {
    onLoadMore (event) {
      if (this.loadMore) {
        this.$emit('loadMore')
      }
    },
    enableFocusStyle (enable) {
      let $el = this.$el
      if (enable === true) {
        $el.setAttribute('tabIndex', '0')
        $el.addEventListener('focus', this.focus)
        $el.addEventListener('blur', this.blur)
      } else {
        $el.removeAttribute('tabindex')
        $el.removeEventListener('focus', this.focus)
        $el.removeEventListener('blur', this.blur)
      }
    },
    focus () {
      if (this.filterable && !this.autoComplete) {
        this.$refs['input'].focus()
      } else {
        this.$el.focus()
      }
      this.isFocused = true
    },
    blur () {
      if (this.filterable) {
        this.$refs.input.blur()
        this.$emit('on-blur')
      } else {
        this.$el.blur()
      }
      this.isFocused = false
    },
    handleFocus () {
      this.focus()
    },
    handleBlur () {
      this.blur()
    },
    getNextValidFocusIndex (startFocusIndex) {
      let index = startFocusIndex + 1
      if (this.filterable) {
        if (index > this.options.length) {
          index = this.getNextValidFocusIndex(0)
        }
        if (this.optionInstances[index - 1].visible === false) {
          index = this.getNextValidFocusIndex(index)
        }
      } else {
        if (index > this.options.length) {
          index = 1
        }
      }
      return index
    },
    getPrevValidFocusIndex (startFocusIndex) {
      let index = startFocusIndex - 1
      if (this.filterable) {
        if (index < 1) {
          index = this.getPrevValidFocusIndex(this.options.length + 1)
        }
        if (this.optionInstances[index - 1].visible === false) {
          index = this.getPrevValidFocusIndex(index)
        }
      } else {
        if (index < 1) {
          index = this.options.length
        }
      }
      return index
    },
    $_toggleMenu () {
      if (this.disabled || this.autoComplete) {
        return false
      }
      this.visible = !this.visible
    },
    // 判断是否为空
    isEmptyValue () {
      let empty = false
      if ((typeof this.model) === 'string') {
        if (this.model === '') {
          empty = true
        }
      } else if (Array.isArray(this.model)) {
        if (!this.model.length) {
          empty = true
        }
      }
      return empty
    },
    onSelfMouseenter () {
      if (!this.clearable || this.showPlaceholder) return false
      this.showArrow = false
    },
    onSelfMouseleave () {
      if (!this.clearable || this.showPlaceholder) return false
      this.showArrow = true
    },
    $_hideMenu () {
      if (this.visible === true) {
        this.visible = false
        this.focusIndex = this.originalFocusIndex
        this.broadcast('TOption', 'select-close')
      }
    },
    // find option component
    $_findChild (cb) {
      const find = function (child) {
        const name = child.$options.componentName
        if (name) {
          cb(child)
        } else if (child.$children.length) {
          child.$children.forEach((innerChild) => {
            find(innerChild, cb)
          })
        }
      }
      if (this.optionInstances.length) {
        this.optionInstances.forEach((child) => {
          find(child)
        })
      } else {
        this.$children.forEach((child) => {
          find(child)
        })
      }
    },
    $_updateOptions (init, slot = false) {
      let options = []
      let index = 1
      if (init) {
        this.optionInstances = []
      }
      this.$_findChild((child) => {
        options.push({
          value: child.value,
          label: (child.label === undefined) ? child.$el.children[0].innerHTML : child.label
        })
        child.index = index++
        if (init) {
          this.optionInstances.push(child)
        }
      })
      this.options = options
      if (init) {
        if (!this.remote) {
          if (this.multiple === true) {
            this.$_updateMultipleSelected(true, slot)
          } else {
            this.$_updateSingleSelected(true, slot)
          }
        }
      }
    },
    $_updateSingleSelected (init = false, slot = false) {
      if (!Array.isArray(this.model)) {
        let findModel = false
        for (let i = 0; i < this.options.length; i++) {
          if (this.model === this.options[ i ].value || this.model.value === this.options[ i ].value) {
            this.selectedSingle = this.options[ i ].label
            findModel = true
            break
          }
        }
        if (slot && !findModel) {
          if (this.labelInValue) {
            this.model = {}
          } else {
            this.model = ''
          }
          this.query = ''
        }
      }
      this._toggleSingleSelected(this.model, init)
    },
    clearSingleSelect () {
      if (this.showCloseIcon) {
        this.$_findChild((child) => {
          child.selected = false
        })
        if (this.labelInValue) {
          this.model = {}
        } else {
          this.model = ''
        }
        if (this.filterable) {
          this.query = ''
        }
        this.focusIndex = 0
        this.originalFocusIndex = 0
        this.showArrow = true
      }
    },
    $_updateMultipleSelected (init = false, slot = false) {
      if (this.multiple && Array.isArray(this.model)) {
        let selected = this.remote ? this.selectedMultiple : []
        for (let i = 0; i < this.model.length; i++) {
          const model = this.model[ i ]
          for (let j = 0; j < this.options.length; j++) {
            const option = this.options[ j ]
            // labelinvalue时model为object
            if (model === option.value || model.value === option.value) {
              selected.push(option)
            }
          }
        }
        const selectedArray = []
        const selectedObject = {}
        selected.forEach(item => {
          if (!selectedObject[ item.value ]) {
            selectedArray.push(item)
            selectedObject[ item.value ] = 1
          }
        })
        this.selectedMultiple = this.remote ? selectedArray : selected
        if (slot) {
          let selectedModel = []
          for (let i = 0; i < selected.length; i++) {
            selectedModel.push(selected[ i ].value)
          }
          // if slot change and remove a selected option, emit user
          if (this.model.length === selectedModel.length) {
            this.slotChangeDuration = true
          }
          this.model = selectedModel
        }
      }
      this._toggleMultipleSelected(this.model, init)
    },
    onClearTagClick (index) {
      this._removeTag(index)
    },
    _removeTag (index) {
      if (this.disabled) {
        return false
      }
      if (this.remote) {
        let tag = this.model[ index ]
        if (this.labelInValue) {
          tag = this.model[ index ].value
        }
        this.selectedMultiple = this.selectedMultiple.filter(item => item.value !== tag)
      }
      this.model.splice(index, 1)
      if (this.filterable) {
        this.focus()
      }
      this.broadcast('TPopBox', 'on-update-popper')
    },
    // to select option for single
    _toggleSingleSelected (value, init = false) {
      if (!this.multiple) {
        this.$_findChild((child) => {
          if (child.value === value) {
            child.selected = true

            if (this.multiple === false) {
              this.focusIndex = this.optionInstances.indexOf(child) + 1
              this.originalFocusIndex = this.focusIndex
            }
          } else {
            child.selected = false
          }
        })
        // this.$_hideMenu()
        if (!init) {
          if (this.labelInValue) {
            this.$emit('on-change', value)
            this.dispatch('TFormItem', 'on-form-change', value)
          } else {
            this.$emit('on-change', value)
            this.dispatch('TFormItem', 'on-form-change', value)
          }
        }
      }
    },
    // to select option for multiple
    _toggleMultipleSelected (value, init = false) {
      if (this.multiple) {
        let vals = []
        this.$_findChild((child) => {
          let flag = false
          for (var i = 0; i < value.length; i++) {
            if (value[i].value === child.value || value[i] === child.value) {
              flag = true
              break
            }
          }
          if (flag) {
            child.selected = true
            vals.push(child.value)
          } else {
            child.selected = false
          }
        })
        if (!init) {
          if (this.labelInValue) {
            this.$emit('on-change', value)
            this.dispatch('TFormItem', 'on-form-change', value)
          } else {
            this.$emit('on-change', vals)
            this.dispatch('TFormItem', 'on-form-change', vals)
          }
        }
      }
    },
    _handleClose () {
      this.$_hideMenu()
    },
    onDocumentKeydown (e) {
      const keyCode = e.keyCode
      if (this.visible) {
        switch (keyCode) {
          case 27: // Esc取消下拉
            e.preventDefault()
            this.$_hideMenu()
            this.focus()
            break
          case 40: // 下键跳转到下一项
            e.preventDefault()
            this.$_navigateOptions('next')
            break
          case 38: // 上键跳转到上一项
            e.preventDefault()
            this.$_navigateOptions('prev')
            break
          case 13: // 回车确定选择
            e.preventDefault()
            this.$_findChild((child) => {
              if (child.isFocus) {
                child.select()
              }
            })
            if (this.multiple === false) {
              this.focus()
            }
            break
        }
      } else {
        if (keyCode === 40 && this.isFocused) {
          this.$_toggleMenu()
        }
      }
      e.cancelBubble = true
    },
    $_navigateOptions (direction) {
      // let index = 0
      if (direction === 'next') {
        // index = this.focusIndex + 1
        // this.focusIndex = (this.focusIndex === this.options.length) ? 1 : index
        this.focusIndex = this.getNextValidFocusIndex(this.focusIndex)
      } else if (direction === 'prev') {
        // index = this.focusIndex - 1
        // this.focusIndex = (this.focusIndex <= 1) ? this.options.length : index
        this.focusIndex = this.getPrevValidFocusIndex(this.focusIndex)
      }
      let childStatus = {
        disabled: false,
        visible: false
      }
      let findDeep = false // can next find allowed
      this.$_findChild((child) => {
        if (child.index === this.focusIndex) {
          childStatus.disabled = child.disabled
          childStatus.visible = child.visible
          if (!child.disabled && child.visible) {
            child.isFocus = true
          }
        } else {
          child.isFocus = false
        }
        if (child.visible && !child.disabled) {
          findDeep = true
        }
      })
      // 此处由于没看懂逻辑先打个补丁
      if (this.remote && this.optionInstances.length === 0) {
        this.$_updateOptions(true)
      }
      this.$_resetScrollTop()
      if ((childStatus.disabled || !childStatus.visible) && findDeep) {
        this.$_navigateOptions(direction)
      }
    },
    $_resetScrollTop () {
      let index = this.focusIndex - 1
      let $optEle = this.optionInstances[ index ].$el
      let $dropEle = this.$refs.drop.$el
      let bottomOverflowDistance = $optEle.getBoundingClientRect().bottom - $dropEle.getBoundingClientRect().bottom
      let topOverflowDistance = $optEle.getBoundingClientRect().top - $dropEle.getBoundingClientRect().top
      if (bottomOverflowDistance > 0) {
        $dropEle.scrollTop += bottomOverflowDistance
      }
      if (topOverflowDistance < 0) {
        $dropEle.scrollTop += topOverflowDistance
      }
    },
    onInputFocus () {
      this.focus()
    },
    onInputBlur () {
      this.blur()
      setTimeout(() => {
        let model = this.model || ''
        if (this.labelInValue) {
          if (this.model && this.model.value) {
            model = this.model.value
          } else {
            model = ''
          }
        }
        if (this.multiple) {
          this.query = ''
        } else {
          if (model !== '') {
            this.$_findChild((child) => {
              if (child.value === model) {
                this.query = child.label === undefined ? child.searchLabel : child.label
              }
            })
            if (this.remote && this.query !== this.lastQuery) {
              this.$nextTick(() => {
                this.query = this.lastQuery
              })
            }
          } else {
            this.query = ''
          }
        }
      }, 300)
    },
    onInputKeydown () {
      this.inputLength = this.$refs.input.value.length * 12 + 20
    },
    onInputDelete () {
      if (this.query === '') {
        if (this.multiple) {
          if (this.model.length > 0 && this.multipleDelete) {
            this._removeTag(this.model.length - 1)
          } else {
            this.multipleDelete = true
          }
        } else {
          if (this.labelInValue) {
            this.model = {}
          } else {
            this.model = ''
          }
        }
      }
      // NOTE: 用和clearale的方式来处理
      this.$nextTick(() => {
        if (!this.query) {
          this.$_findChild((child) => {
            child.selected = false
          })
          if (this.filterable) {
            this.query = ''
          }
          this.focusIndex = 0
          this.originalFocusIndex = 0
          this.showArrow = true
        }
      })
    },
    // use when slot changed
    $_slotChange () {
      this.options = []
      this.optionInstances = []
    },
    setQuery (query) {
      if (!this.filterable) return
      this.query = query
    },
    $_modelToQuery () {
      if (!this.multiple && this.filterable && this.model !== undefined) {
        let val = this.model
        if (this.labelInValue) {
          val = this.model.value
        }
        this.$_findChild((child) => {
          if (val === child.value) {
            if (child.label) {
              this.query = child.label
            } else if (child.searchLabel) {
              this.query = child.searchLabel
            } else {
              this.query = child.value
            }
          }
        })
      }
    },
    $_broadcastQuery (val) {
      if (this.filterable) {
        this.broadcast('TOption', 'query-change', val)
      }
    },
    setScrollHide () {
      /**
       * 绑定一个事件判断当前的reference 是否在可视区域内
       */
      let clientW = document.documentElement.clientWidth
      let clientH = document.documentElement.clientHeight
      let that = this
      let target = this.$el
      window.addEventListener('scroll', () => {
        let poperRef = target.getBoundingClientRect()
        if (!(poperRef.top > 0 && poperRef.top <= clientH && poperRef.left > 0 && poperRef.left <= clientW)) {
          /* 此时不在可视区域内 */
          that.visible = false
        }
      })
    },
    hasGroup () {
      let $children = this.$children
      return ($children.length > 0 && $children[0].name === 'TOptionGroup')
    }
  }
}
</script>



// WEBPACK FOOTER //
// select.vue?696422ea